import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import moment from "moment-timezone";
import { Copy, Pencil, FileEdit, Clock, CircleDot, CircleCheck, ListChecks, Square, Undo2 } from "lucide-react";
import Button from "components/shared/Button";
import { Badge } from "components/ui/badge";
import { useCompanyDateFormat } from "utils/hooks/company";
import { cn } from "lib/utils";
import { getWindowStatus, type SelfSchedulingWindow, type WindowStatus } from "./data";

export const STATUS_CONFIG: Record<WindowStatus, { label: string; className: string; icon: typeof Clock }> = {
    draft: { label: "draft", className: "tw-bg-neutral-100 tw-text-neutral-600 tw-border-neutral-200", icon: FileEdit },
    scheduled: { label: "scheduled", className: "tw-bg-blue-50 tw-text-blue-700 tw-border-blue-200", icon: Clock },
    open: { label: "open", className: "tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200", icon: CircleDot },
    closed: { label: "closed", className: "tw-bg-neutral-100 tw-text-neutral-600 tw-border-neutral-200", icon: CircleCheck },
};

function StatusBadge({ status }: { status: WindowStatus }) {
    const { t } = useTranslation();
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;

    return (
        <Badge variant="outline" className={cn("tw-gap-1.5", config.className)}>
            <Icon className="tw-h-3 tw-w-3" />
            {t(config.label)}
        </Badge>
    );
}

interface ColumnProps {
    onViewDetails?: (window: SelfSchedulingWindow) => void;
    onDuplicate?: (window: SelfSchedulingWindow) => void;
    onCloseEarly?: (window: SelfSchedulingWindow) => void;
    onUnpublish?: (window: SelfSchedulingWindow) => void;
    onClaimActivity?: (window: SelfSchedulingWindow) => void;
}

export const useColumns = ({ onViewDetails, onDuplicate, onCloseEarly, onUnpublish, onClaimActivity }: ColumnProps) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();

    return useMemo<ColumnDef<SelfSchedulingWindow>[]>(() => [
        {
            accessorKey: "name",
            header: t("name"),
        },
        {
            accessorKey: "department",
            header: t("department"),
        },
        {
            accessorKey: "unit",
            header: t("unit"),
        },
        {
            id: "targetPeriod",
            accessorFn: (row) => `${row.targetPeriodFrom} - ${row.targetPeriodTo}`,
            header: t("target-period"),
            enableSorting: false,
            cell: ({ row }) => (
                <span className="tw-whitespace-nowrap">
                    {moment(row.original.targetPeriodFrom).format(dateFormat)} – {moment(row.original.targetPeriodTo).format(dateFormat)}
                </span>
            ),
        },
        {
            id: "window",
            accessorFn: (row) => `${row.openDate} - ${row.closeDate}`,
            header: t("self-scheduling-window"),
            enableSorting: false,
            cell: ({ row }) => (
                <span className="tw-whitespace-nowrap">
                    {moment(row.original.openDate).format(dateFormat)} – {moment(row.original.closeDate).format(dateFormat)}
                </span>
            ),
        },
        {
            id: "shifts",
            accessorFn: (row) => row.shiftIds.length,
            header: t("shifts"),
            enableSorting: false,
            cell: ({ row }) => (
                <span className="tw-whitespace-nowrap tw-text-muted-foreground">
                    {row.original.shiftIds.length}
                </span>
            ),
        },
        {
            id: "status",
            accessorFn: (row) => getWindowStatus(row),
            header: t("status"),
            enableSorting: false,
            cell: ({ row }) => <StatusBadge status={getWindowStatus(row.original)} />,
        },
        {
            id: "actions",
            enableSorting: false,
            enableHiding: false,
            header: () => <div className="tw-text-right">{t("actions")}</div>,
            cell: ({ row: tableRow }) => {
                const row = tableRow.original;
                const status = getWindowStatus(row);

                return (
                    <div className="tw-flex tw-justify-end">
                        <Button
                            variant="ghost"
                            size="icon"
                            tooltip={t("edit")}
                            onClick={() => onViewDetails?.(row)}
                        >
                            <Pencil className="tw-h-4 tw-w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            tooltip={t("duplicate")}
                            onClick={() => onDuplicate?.(row)}
                        >
                            <Copy className="tw-h-4 tw-w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            tooltip={t("claim-activity")}
                            onClick={() => onClaimActivity?.(row)}
                        >
                            <ListChecks className="tw-h-4 tw-w-4" />
                        </Button>
                        {status === "open" && (
                            <Button
                                variant="ghost"
                                size="icon"
                                tooltip={t("close-early")}
                                onClick={() => onCloseEarly?.(row)}
                            >
                                <Square className="tw-h-4 tw-w-4" />
                            </Button>
                        )}
                        {row.published && (
                            <Button
                                variant="ghost"
                                size="icon"
                                tooltip={t("unpublish")}
                                onClick={() => onUnpublish?.(row)}
                            >
                                <Undo2 className="tw-h-4 tw-w-4" />
                            </Button>
                        )}
                    </div>
                );
            },
        },
    ], [t, dateFormat, onViewDetails, onDuplicate, onCloseEarly, onUnpublish, onClaimActivity]);
};
